import { useQuery } from '@tanstack/react-query';
import { multiPolygon, points } from '@turf/helpers';
import pointsWithinPolygon from '@turf/points-within-polygon';
import { Position } from 'deck.gl';
import { useMemo } from 'react';
import Api from '../../../../../api/Api';
import { QUERY_KEYS } from '../../../../../queries/queryKeys';
import { useOverlayShapeFile } from '../../../../../queries/useOverlayQuery';
import { THOWDWAVESPECTRA } from '../../../../../utils/common';

export const useWaveSpectraCoverage = (datasetId: string, coords: number[]) => {
  const [longitude, latitude] = coords;

  const shapeFileQuery = useOverlayShapeFile(datasetId);

  const spectraShape = useMemo(
    () => shapeFileQuery.data?.find((shapeFile) => shapeFile.Level2 === THOWDWAVESPECTRA),
    [shapeFileQuery.data]
  );

  const geoJsonQuery = useQuery({
    queryKey: [
      QUERY_KEYS.map.overlay.geoJson,
      datasetId,
      spectraShape?.Level1,
      spectraShape?.Level2,
    ],
    queryFn: () =>
      Api.fileEndPoint.getOverlayGeoJson(
        datasetId,
        spectraShape!.Level1,
        spectraShape!.Level2,
        spectraShape!.LegendColor[0]
      ),
    enabled: !!spectraShape,
  });

  const hasCoverage = useMemo(() => {
    if (!shapeFileQuery.data) return null;
    if (!spectraShape) return false;
    if (!geoJsonQuery.data) return null;
    if (!geoJsonQuery.data.length) return false;

    const multipolygon = multiPolygon(
      geoJsonQuery.data.map((poly) => [poly.coordinates as Position[]])
    );
    const activePoint = points([[longitude, latitude]]);
    const within = pointsWithinPolygon(activePoint, multipolygon);
    return within.features.length > 0;
  }, [shapeFileQuery.data, spectraShape, geoJsonQuery.data, longitude, latitude]);

  return {
    hasCoverage,
    isChecking: shapeFileQuery.isLoading || (!!spectraShape && geoJsonQuery.isLoading),
  };
};
